package fr.frux.core.common.share

import fr.frux.core.common.logging.Logger
import kotlinx.browser.window
import kotlinx.coroutines.await
import kotlin.js.Promise

private const val TAG = "ShareLauncher"

/**
 * `navigator.share` (Web Share API) quand disponible : l'API n'existe ni sur Firefox desktop ni
 * sur les navigateurs sans contexte sécurisé, d'où le repli presse-papiers plutôt qu'un échec sec.
 *
 * `asDynamic()` : ni `navigator.share` ni `navigator.clipboard` ne sont déclarés dans les bindings
 * Kotlin/JS de `Navigator` à ce jour.
 */
private object JsShareLauncher : ShareLauncher {
    // Frontière JS : une promesse rejetée par le navigateur peut porter n'importe quel type
    // d'erreur (même justification que PhotoUploadPrep.js.kt), un catch ciblé laisserait
    // planter l'appelant pour un simple refus de partage.
    @Suppress("TooGenericExceptionCaught")
    override suspend fun share(
        title: String,
        text: String,
        url: String,
    ): ShareOutcome {
        val nav = window.navigator.asDynamic()
        val shareFn = nav.share
        if (shareFn == null) {
            return copyToClipboard(text, url)
        }
        val payload = js("({})")
        payload.title = title
        payload.text = text
        payload.url = url
        return try {
            (shareFn.call(nav, payload) as Promise<*>).await()
            ShareOutcome.SHARED
        } catch (e: Throwable) {
            val name = (e.asDynamic().name as? String).orEmpty()
            if (name == "AbortError") {
                ShareOutcome.CANCELLED
            } else {
                Logger.d(TAG, "navigator.share a échoué ($name), repli presse-papiers")
                copyToClipboard(text, url)
            }
        }
    }

    @Suppress("TooGenericExceptionCaught")
    private suspend fun copyToClipboard(
        text: String,
        url: String,
    ): ShareOutcome {
        val clipboard = window.navigator.asDynamic().clipboard
        if (clipboard == null) {
            Logger.e(TAG, "Aucune API de presse-papiers disponible")
            return ShareOutcome.FAILED
        }
        return try {
            (clipboard.writeText("$text\n$url") as Promise<*>).await()
            ShareOutcome.COPIED
        } catch (e: Throwable) {
            Logger.e(TAG, "Échec de la copie dans le presse-papiers", e)
            ShareOutcome.FAILED
        }
    }
}

actual fun createShareLauncher(): ShareLauncher = JsShareLauncher
